import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { AppLayout } from "../shared/layout";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { changePassword, deleteUser, updateUser, updatePreferences } from "../../../data";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { toast } from "@/components/ui/use-toast";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import AuthStore from "../../../stores/AuthStore";
import { useNavigate } from "react-router-dom";
import { User, Bell, Shield, Palette, LogOut, Lock } from "lucide-react";
import { PrivacySettings } from "@/components/settings/privacy-settings";
import { applyTheme } from "@/hooks/use-theme";

export function SettingsScreen() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const user = AuthStore.user;

  const [profileData, setProfileData] = useState({
    name: user?.name || "",
    email: user?.email || "",
  });

  const [passwordData, setPasswordData] = useState({
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
  });

  const [notifications, setNotifications] = useState({
    notificationEmailEnabled: user?.notificationEmailEnabled ?? true,
    notificationPushEnabled: user?.notificationPushEnabled ?? true,
    notificationMarketing: user?.notificationMarketing ?? true,
  });

  const [appearance, setAppearance] = useState({
    themeMode: user?.themeMode || "system",
    preferredLanguage: user?.preferredLanguage || "en",
  });

  // Mutations
  const updateProfileMutation = useMutation({
    mutationFn: (data) => updateUser(user?.id, data),
    onSuccess: (data) => {
      AuthStore.setProfile(data.data);
      toast({ title: t("common.success"), description: t("profile.profileUpdated"), variant: "success" });
    },
    onError: (error) => {
      toast({ title: t("common.error"), description: error.response?.data?.message || t("profile.updateFailed"), variant: "destructive" });
    },
  });

  const updatePreferencesMutation = useMutation({
    mutationFn: (data) => updatePreferences(data),
    onSuccess: (data) => {
      // Update AuthStore with new preferences
      if (data?.data) {
        AuthStore.setProfile({ ...AuthStore.user, ...data.data });
      }
      queryClient.invalidateQueries({ queryKey: ["profile"] });
    },
    onError: (error) => {
      toast({ 
        title: t("common.error"), 
        description: error.response?.data?.message || t("profile.updateFailed"), 
        variant: "destructive" 
      });
    },
  });

  const changePasswordMutation = useMutation({
    mutationFn: changePassword,
    onSuccess: () => {
      toast({ title: t("common.success"), description: t("changePassword.success"), variant: "success" });
      setPasswordData({ currentPassword: "", newPassword: "", confirmPassword: "" });
    },
    onError: (error) => {
      toast({ title: t("common.error"), description: error.response?.data?.message || t("changePassword.error"), variant: "destructive" });
    },
  });

  const deleteAccountMutation = useMutation({
    mutationFn: () => deleteUser(user?.id),
    onSuccess: () => {
      AuthStore.logout();
      navigate("/");
      toast({ title: t("deleteAccount.successTitle"), description: t("deleteAccount.successMessage") });
    },
    onError: () => {
      toast({ title: t("common.error"), description: t("deleteAccount.errorMessage"), variant: "destructive" });
    },
  });

  // Handlers
  const handleProfileUpdate = (e) => {
    e.preventDefault();
    updateProfileMutation.mutate({ name: profileData.name });
  };

  const handlePasswordChange = (e) => {
    e.preventDefault();
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      toast({ title: t("common.error"), description: t("changePassword.passwordMismatch"), variant: "destructive" });
      return;
    }
    changePasswordMutation.mutate({
      oldPassword: passwordData.currentPassword,
      newPassword: passwordData.newPassword,
    });
  };

  const handleNotificationChange = (key) => {
    const newValue = !notifications[key];
    const oldValue = notifications[key];
    
    // Optimistic update
    setNotifications(prev => ({ ...prev, [key]: newValue }));
    
    // Update backend using preferences endpoint (no permission required)
    updatePreferencesMutation.mutate(
      { [key]: newValue },
      {
        onError: (error) => {
          // Revert on error
          setNotifications(prev => ({ ...prev, [key]: oldValue }));
          toast({
            title: t("common.error"),
            description: error.response?.data?.message || t("settings.notificationUpdateError", { defaultValue: "Bildirim ayarı güncellenemedi" }),
            variant: "destructive",
          });
        },
        onSuccess: () => {
          toast({
            title: t("common.success"),
            description: t("settings.notificationUpdated", { defaultValue: "Bildirim ayarı güncellendi" }),
          });
        },
      }
    );
  };

  const handleThemeChange = (mode) => {
    setAppearance(prev => ({ ...prev, themeMode: mode }));
    // Apply theme immediately
    applyTheme(mode);
    // Save to backend using preferences endpoint (no permission required)
    updatePreferencesMutation.mutate({ themeMode: mode });
  };

  return (
    <AppLayout
      title={t("settings.title")}
      description={t("settings.subtitle")}
    >
      <Tabs defaultValue="account" className="max-w-4xl">
        <TabsList className="grid w-full grid-cols-5 mb-8">
          <TabsTrigger value="account" className="flex items-center gap-2">
            <User className="h-4 w-4" /> {t("settings.account")}
          </TabsTrigger>
          <TabsTrigger value="notifications" className="flex items-center gap-2">
            <Bell className="h-4 w-4" /> {t("settings.notifications")}
          </TabsTrigger>
          <TabsTrigger value="privacy" className="flex items-center gap-2">
            <Lock className="h-4 w-4" /> {t("settings.privacy")}
          </TabsTrigger>
          <TabsTrigger value="appearance" className="flex items-center gap-2">
            <Palette className="h-4 w-4" /> {t("settings.appearance")}
          </TabsTrigger>
          <TabsTrigger value="security" className="flex items-center gap-2">
            <Shield className="h-4 w-4" /> {t("settings.security")}
          </TabsTrigger>
        </TabsList>

        {/* Account Tab */}
        <TabsContent value="account" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>{t("profile.profileInfo")}</CardTitle>
              <CardDescription>{t("profile.updateInfo")}</CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleProfileUpdate} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="name">{t("profile.displayName")}</Label>
                  <Input
                    id="name"
                    value={profileData.name}
                    onChange={(e) => setProfileData({ ...profileData, name: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="email">{t("auth.email")}</Label>
                  <Input
                    id="email"
                    value={profileData.email}
                    disabled
                    className="bg-muted"
                  />
                  <p className="text-xs text-muted-foreground">{t("profile.emailCannotChange")}</p>
                </div>
                <div className="flex justify-end">
                  <Button type="submit" disabled={updateProfileMutation.isLoading}>
                    {updateProfileMutation.isLoading ? t("common.saving") : t("common.saveChanges")}
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>

          <Card className="border-red-100">
            <CardHeader>
              <CardTitle className="text-red-600">{t("deleteAccount.dangerZone")}</CardTitle>
              <CardDescription>{t("deleteAccount.dangerZoneDesc")}</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex items-center justify-between">
                <div>
                  <h4 className="font-medium">{t("deleteAccount.title")}</h4>
                  <p className="text-sm text-muted-foreground">{t("deleteAccount.description")}</p>
                </div>
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button variant="destructive">{t("deleteAccount.button")}</Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle>{t("deleteAccount.confirmTitle")}</AlertDialogTitle>
                      <AlertDialogDescription>
                        {t("deleteAccount.confirmMessage")}
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel>{t("common.cancel")}</AlertDialogCancel>
                      <AlertDialogAction 
                        onClick={() => deleteAccountMutation.mutate()}
                        className="bg-red-600 hover:bg-red-700"
                      >
                        {deleteAccountMutation.isLoading ? t("common.deleting") : t("deleteAccount.confirmButton")}
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Notifications Tab */}
        <TabsContent value="notifications">
          <Card>
            <CardHeader>
              <CardTitle>{t("settings.notificationPreferences")}</CardTitle>
              <CardDescription>{t("settings.notificationPreferencesDesc")}</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="flex items-center justify-between">
                <div className="space-y-0.5">
                  <Label>{t("settings.emailNotifications")}</Label>
                  <p className="text-sm text-muted-foreground">{t("settings.emailNotificationsDesc")}</p>
                </div>
                <Switch
                  checked={notifications.notificationEmailEnabled}
                  onCheckedChange={() => handleNotificationChange("notificationEmailEnabled")}
                />
              </div>
              <div className="flex items-center justify-between">
                <div className="space-y-0.5">
                  <Label>{t("settings.pushNotifications")}</Label>
                  <p className="text-sm text-muted-foreground">{t("settings.pushNotificationsDesc")}</p>
                </div>
                <Switch
                  checked={notifications.notificationPushEnabled}
                  onCheckedChange={() => handleNotificationChange("notificationPushEnabled")}
                />
              </div>
              <div className="flex items-center justify-between">
                <div className="space-y-0.5">
                  <Label>{t("settings.marketingEmails")}</Label>
                  <p className="text-sm text-muted-foreground">{t("settings.marketingEmailsDesc")}</p>
                </div>
                <Switch
                  checked={notifications.notificationMarketing}
                  onCheckedChange={() => handleNotificationChange("notificationMarketing")}
                />
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Privacy Tab */}
        <TabsContent value="privacy">
          <PrivacySettings />
        </TabsContent>

        {/* Appearance Tab */}
        <TabsContent value="appearance">
          <Card>
            <CardHeader>
              <CardTitle>{t("settings.appearance")}</CardTitle>
              <CardDescription>{t("settings.appearanceDesc")}</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="space-y-2">
                <Label>{t("settings.theme")}</Label>
                <div className="grid grid-cols-3 gap-4">
                  <div 
                    className={`border-2 rounded-lg p-4 cursor-pointer hover:bg-accent ${appearance.themeMode === 'light' ? 'border-primary' : 'border-transparent'}`}
                    onClick={() => handleThemeChange('light')}
                  >
                    <div className="h-20 bg-white border rounded mb-2"></div>
                    <p className="text-center font-medium">{t("settings.light")}</p>
                  </div>
                  <div 
                    className={`border-2 rounded-lg p-4 cursor-pointer hover:bg-accent ${appearance.themeMode === 'dark' ? 'border-primary' : 'border-transparent'}`}
                    onClick={() => handleThemeChange('dark')}
                  >
                    <div className="h-20 bg-slate-950 border rounded mb-2"></div>
                    <p className="text-center font-medium">{t("settings.dark")}</p>
                  </div>
                  <div 
                    className={`border-2 rounded-lg p-4 cursor-pointer hover:bg-accent ${appearance.themeMode === 'system' ? 'border-primary' : 'border-transparent'}`}
                    onClick={() => handleThemeChange('system')}
                  >
                    <div className="h-20 bg-gradient-to-r from-white to-slate-950 border rounded mb-2"></div>
                    <p className="text-center font-medium">{t("settings.system")}</p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Security Tab */}
        <TabsContent value="security">
          {!user?.googleId ? (
            <Card>
              <CardHeader>
                <CardTitle>{t("changePassword.title")}</CardTitle>
                <CardDescription>{t("changePassword.subtitle")}</CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handlePasswordChange} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="currentPassword">{t("changePassword.currentPassword")}</Label>
                    <Input
                      id="currentPassword"
                      type="password"
                      value={passwordData.currentPassword}
                      onChange={(e) => setPasswordData({ ...passwordData, currentPassword: e.target.value })}
                      required
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="newPassword">{t("changePassword.newPassword")}</Label>
                      <Input
                        id="newPassword"
                        type="password"
                        value={passwordData.newPassword}
                        onChange={(e) => setPasswordData({ ...passwordData, newPassword: e.target.value })}
                        required
                        minLength={6}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="confirmPassword">{t("changePassword.confirmPassword")}</Label>
                      <Input
                        id="confirmPassword"
                        type="password"
                        value={passwordData.confirmPassword}
                        onChange={(e) => setPasswordData({ ...passwordData, confirmPassword: e.target.value })}
                        required
                        minLength={6}
                      />
                    </div>
                  </div>
                  <div className="flex justify-end">
                    <Button type="submit" disabled={changePasswordMutation.isLoading}>
                      {changePasswordMutation.isLoading ? t("common.updating") : t("changePassword.updatePassword")}
                    </Button>
                  </div>
                </form>
              </CardContent>
            </Card>
          ) : (
            <Card>
              <CardHeader>
                <CardTitle>{t("auth.password")}</CardTitle>
                <CardDescription>{t("settings.googlePasswordNote")}</CardDescription>
              </CardHeader>
            </Card>
          )}
          
          <div className="mt-6 flex justify-end">
             <Button variant="outline" onClick={() => {
               queryClient.clear();
               AuthStore.logout();
               navigate("/");
             }}>
               <LogOut className="mr-2 h-4 w-4" /> {t("settings.logout")}
             </Button>
          </div>
        </TabsContent>
      </Tabs>
    </AppLayout>
  );
}
